import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { AuthService } from './auth.service';

export interface Permiso {
  modulo: string;
  accion: string;
}

@Injectable({
  providedIn: 'root'
})
export class PermisosService {
  private apiUrl = '/api';
  private permisosSubject = new BehaviorSubject<Permiso[]>([]);
  public permisos$ = this.permisosSubject.asObservable();
  private permisosReady = false;
  private loadRequest: Observable<Permiso[]> | null = null;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {
    // Cargar permisos cuando el usuario esté autenticado
    this.authService.currentUser$.subscribe(user => {
      if (user) {
        this.cargarPermisosSync();
      } else {
        this.resetPermisos();
      }
    });

    // También escuchar el evento de login
    window.addEventListener('userLoggedIn', () => {
      if (this.authService.isAuthenticated()) {
        this.permisosReady = false;
        this.loadRequest = null;
        this.cargarPermisosSync();
      }
    });

    // Cargar permisos si ya está autenticado al iniciar
    if (this.authService.isAuthenticated()) {
      this.cargarPermisosSync();
    }
  }

  private resetPermisos(): void {
    this.permisosSubject.next([]);
    this.permisosReady = false;
    this.loadRequest = null;
  }

  private marcarPermisosListos(permisos: Permiso[]): void {
    this.permisosSubject.next(permisos);
    this.permisosReady = true;
  }

  /** Espera a que los permisos estén cargados antes de evaluar guards (p. ej. pestaña nueva). */
  whenReady(): Observable<void> {
    if (!this.authService.isAuthenticated()) {
      return of(undefined);
    }
    if (this.permisosReady) {
      return of(undefined);
    }
    if (!this.loadRequest) {
      this.loadRequest = this.fetchPermisos().pipe(
        shareReplay(1),
        finalize(() => {
          this.loadRequest = null;
        })
      );
    }
    return this.loadRequest.pipe(map(() => undefined));
  }

  private fetchPermisos(): Observable<Permiso[]> {
    const user = this.authService.getUser();
    if (!user) {
      this.permisosReady = true;
      this.permisosSubject.next([]);
      return of([]);
    }

    return this.http.get<Permiso[]>(`${this.apiUrl}/usuarios/${user.id}/permisos`).pipe(
      tap(permisos => this.marcarPermisosListos(permisos)),
      catchError(err => {
        console.error('Error al cargar permisos:', err);
        if (this.authService.isAuthFailure(err)) {
          this.authService.handleUnauthorizedSession();
        }
        this.marcarPermisosListos([]);
        return of([]);
      })
    );
  }

  cargarPermisos(): Observable<Permiso[]> {
    const user = this.authService.getUser();
    if (!user) {
      this.resetPermisos();
      return of([]);
    }

    return this.fetchPermisos();
  }

  cargarPermisosSync(): void {
    if (!this.authService.isAuthenticated()) {
      this.resetPermisos();
      return;
    }
    if (this.permisosReady || this.loadRequest) {
      return;
    }
    this.loadRequest = this.fetchPermisos().pipe(
      shareReplay(1),
      finalize(() => {
        this.loadRequest = null;
      })
    );
    this.loadRequest.subscribe();
  }

  tienePermiso(modulo: string, accion: string): boolean {
    const permisos = this.permisosSubject.value;
    
    // Verificar si tiene permiso de administrador
    const esAdmin = permisos.some(p => p.modulo === '*' && p.accion === '*');
    if (esAdmin) return true;

    // Verificar permiso específico
    return permisos.some(p => p.modulo === modulo && p.accion === accion);
  }

  tienePermisoModulo(modulo: string): boolean {
    const permisos = this.permisosSubject.value;
    
    // Verificar si tiene permiso de administrador
    const esAdmin = permisos.some(p => p.modulo === '*' && p.accion === '*');
    if (esAdmin) return true;

    // Verificar si tiene cualquier permiso del módulo
    return permisos.some(p => p.modulo === modulo);
  }

  getPermisos(): Permiso[] {
    return this.permisosSubject.value;
  }
}

